import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { assertNumber } from 'helpers/utils/checkType';

import css from './filesReader.module.sass';

interface Props {
  disabled?: boolean;
  multiple?: boolean;
  accept?: string;
  className?: string;
  allowedMIMETypes?: string[];
  maxSizeInMb?: number;
  onLoadStart?: ({
    file,
    readyState,
    timestamp,
  }: {
    file: File;
    readyState: number;
    timestamp: number;
  }) => void;
  onError?: ({ error }: { error: DOMException | null }) => void;
  onAbort?: ({ error }: { error: DOMException | null }) => void;
  onLoadFinish?: ({
    file,
    readyState,
    timestamp,
  }: {
    file: File;
    readyState: number;
    timestamp: number;
  }) => void;
  onValidationError?: (errors: string[]) => void;
}

export const FilesReader: React.FC<Props> = ({
  disabled,
  multiple = true,
  accept = 'image/*',
  className,
  allowedMIMETypes,
  maxSizeInMb,
  onLoadStart,
  onError,
  onAbort,
  onLoadFinish,
  onValidationError,
  children,
}) => {
  const { t } = useTranslation('common');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    const validationErrors: string[] = [];

    if (!files || (Array.isArray(files) && files.length === 0)) {
      return;
    }

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (
        Array.isArray(allowedMIMETypes) &&
        !allowedMIMETypes.includes(file.type)
      ) {
        validationErrors.push(
          t('validations.unsupportedFormat', { format: file.type })
        );
      }

      if (assertNumber(maxSizeInMb) && file.size / 1024 / 1024 > maxSizeInMb) {
        validationErrors.push(
          t('validations.maxFileSize', { size: maxSizeInMb })
        );
      }

      if (validationErrors.length > 0 && onValidationError) {
        onValidationError(validationErrors);

        // eslint-disable-next-line no-continue
        continue;
      }

      const fileReader = new FileReader();
      const timestamp = Date.now();

      fileReader.onloadstart = () =>
        onLoadStart &&
        onLoadStart({ file, timestamp, readyState: fileReader.readyState });
      fileReader.onerror = () =>
        onError && onError({ error: fileReader.error });
      fileReader.onabort = () =>
        onAbort && onAbort({ error: fileReader.error });
      fileReader.onload = () =>
        onLoadFinish &&
        onLoadFinish({ file, timestamp, readyState: fileReader.readyState });

      if (file) {
        fileReader.readAsDataURL(file);
      }

      // eslint-disable-next-line sonarjs/no-collapsible-if
      if (!multiple) {
        if (inputRef.current) {
          inputRef.current.value = '';

          if (!/safari/i.test(navigator.userAgent)) {
            inputRef.current.type = '';
            inputRef.current.type = 'file';
          }
        }
      }
    }
  };

  return (
    <label className={classNames(css.root, className)}>
      {children}
      <input
        ref={inputRef}
        type="file"
        name="file-reader"
        accept={accept}
        multiple={multiple}
        className={css.input}
        disabled={disabled}
        onChange={handleChange}
      />
    </label>
  );
};
